/**
 * Protected Route Component
 * Guards routes to only allow authenticated users. Users holding a temporary
 * password (mustChangePassword) are steered to the change-password screen and
 * kept off every other protected route until they change it.
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { UserRole } from '../api/types';
import { CHANGE_PASSWORD_PATH } from '../api/client';
import { PORTAL_HOME_PATH } from '../utils/portal';
import { ACCOUNT_SECURITY_PATH } from '../api/mfaTypes';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: UserRole | UserRole[];
  /** Where a signed-out visitor is sent (the portal routes use the portal login). Default /login. */
  loginPath?: string;
  /**
   * Routes shared by every signed-in role (change password). Without it, a
   * route with no `requiredRole` is the staff app: a CUSTOMER_PORTAL user is
   * sent to /portal instead (tracker item 45). A route with a `requiredRole`
   * that excludes the role shows Access Denied, as before.
   */
  allowPortalUsers?: boolean;
}

export function ProtectedRoute({ children, requiredRole, loginPath = '/login', allowPortalUsers = false }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { pathname } = useLocation();

  // While loading auth state, show nothing
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to={loginPath} replace />;
  }

  // The staff app is closed to portal users: a route with no role gate (the AppLayout shell and
  // so every staff page inside it) sends them to their own area. Role-gated routes keep their
  // Access Denied below (portal users are never in a staff role list).
  if (user?.role === UserRole.CUSTOMER_PORTAL && !requiredRole && !allowPortalUsers) {
    return <Navigate to={PORTAL_HOME_PATH} replace />;
  }

  // Temporary password: nothing but the change-password screen until cleared
  // (the backend 403s every other protected route anyway).
  if (user?.mustChangePassword && pathname !== CHANGE_PASSWORD_PATH) {
    return <Navigate to={CHANGE_PASSWORD_PATH} replace />;
  }

  // Company requires MFA and it is not set up (tracker item 63): only the
  // security page until it is (the backend 403s the rest anyway). The
  // temporary password gate above comes first (the MFA routes refuse a
  // mustChangePassword user too), so never redirect while it applies.
  if (user?.mfaEnrollmentRequired && !user.mustChangePassword && pathname !== ACCOUNT_SECURITY_PATH) {
    return <Navigate to={ACCOUNT_SECURITY_PATH} replace />;
  }

  // Check role if required
  if (requiredRole && user) {
    const roles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];

    if (!roles.includes(user.role)) {
      return (
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
            <p className="text-gray-600">You don't have permission to access this page.</p>
          </div>
        </div>
      );
    }
  }

  return <>{children}</>;
}
